import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiCalendar, FiX, FiPlus, FiLock, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyForm = { entity: '', name: '', start_date: '', end_date: '' };

const FiscalPeriods = () => {
  const { isFinanceLeadership } = useAuth();
  const [entities, setEntities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [checklist, setChecklist] = useState({}); // periodId -> checklist data
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, perRes] = await Promise.all([financeAPI.entities.list(), financeAPI.periods.list()]);
      setEntities(entRes.data.results || entRes.data || []);
      setPeriods(perRes.data.results || perRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load fiscal periods.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.periods.create(form);
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create period.');
    } finally {
      setSaving(false);
    }
  };

  const viewChecklist = async (period) => {
    setBusyId(period.id);
    try {
      const res = await financeAPI.periods.closeChecklist(period.id);
      setChecklist((prev) => ({ ...prev, [period.id]: res.data }));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load the close checklist.');
    } finally {
      setBusyId(null);
    }
  };

  const closePeriod = async (period) => {
    if (!window.confirm(`Close ${period.name}? This cannot be undone without a joint reopening request.`)) return;
    setBusyId(period.id);
    setError(null);
    try {
      await financeAPI.periods.close(period.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Cannot close this period yet — see the checklist.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiCalendar size={22} />Fiscal Periods</h1>
          <p style={fs.pageSubtitle}>Only OPEN periods accept postings. Closing requires the month-end checklist to be clear.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Period</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}>
            <FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div>
            <div style={fs.label}>Entity</div>
            <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Name</div>
            <input required placeholder="2026-07" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div>
            <div style={fs.label}>Start Date</div>
            <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div>
            <div style={fs.label}>End Date</div>
            <input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create'}</button>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : periods.length === 0 ? (
        <div style={fs.emptyState}><FiCalendar size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No fiscal periods yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {periods.map((p) => {
            const cl = checklist[p.id];
            const busy = busyId === p.id;
            const color = fs.STATUS_COLORS[p.state] || '#888';
            return (
              <div key={p.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{p.name}</span>
                      <span style={fs.badge(color)}>{p.state}</span>
                      <span style={{ color: fs.colors.textFaint, fontSize: 12 }}>{entityName(p.entity)}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>{p.start_date} → {p.end_date}</div>
                  </div>
                  {p.state === 'OPEN' && isFinanceLeadership() && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => viewChecklist(p)} disabled={busy} style={fs.neutralButton}>Check Close Readiness</button>
                      <button onClick={() => closePeriod(p)} disabled={busy} style={fs.dangerButton}><FiLock size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Close Period</button>
                    </div>
                  )}
                </div>
                {cl && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${fs.colors.cardBorder}`, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                    <ChecklistItem label="Unreconciled bank lines" value={cl.unreconciled_bank_lines_count} />
                    <ChecklistItem label="Pending expense approvals" value={cl.pending_expense_approvals_count} />
                    <ChecklistItem label="Pending intercompany approvals" value={cl.pending_intercompany_approvals_count} />
                    <ChecklistItem label="Unposted recurring entries" value={cl.unposted_recurring_entries_count} />
                    {cl.is_clear && (
                      <span style={{ color: fs.colors.green, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <FiCheckCircle size={14} />Ready to close
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ChecklistItem = ({ label, value }) => (
  <div>
    <span style={{ color: value > 0 ? fs.colors.amber : fs.colors.green, fontWeight: 700 }}>{value}</span>
    <span style={{ color: fs.colors.textFaint, marginLeft: 6 }}>{label}</span>
  </div>
);

export default FiscalPeriods;
